import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { format, isWithinInterval, addHours } from "date-fns";
import { vi } from "date-fns/locale";
import { Clock, AlertTriangle, FileText, CheckCircle2, Sun, BarChart3 } from "lucide-react";
import { PortalLayout } from "@/components/portal-layout";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth, useDisplayInfo } from "@/lib/auth";

export const Route = createFileRoute("/teacher/")({ component: TeacherDashboard });

type Session = { id: string; time: string; name: string; room: string; starts_at: Date; ends_at: Date };
type Activity = { Icon: React.ComponentType<{ className?: string }>; iconColor: string; text: string; time: string };
type AttentionStudent = { id: string; name: string; reasons: string[] };
type DueSoon = { title: string; className: string; daysLeft: number };

function timeAgo(d: Date) {
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "vừa xong";
  if (m < 60) return `${m} phút trước`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} giờ trước`;
  const days = Math.floor(h / 24);
  if (days === 1) return "hôm qua";
  return `${days} ngày trước`;
}

function TeacherDashboard() {
  const { user } = useAuth();
  const display = useDisplayInfo();
  const [sessions, setSessions] = React.useState<Session[]>([]);
  const [upcoming, setUpcoming] = React.useState<{ id: string; name: string; starts_at: Date; ends_at: Date; room: string | null }[]>([]);
  const [activity, setActivity] = React.useState<Activity[]>([]);
  const [pendingGrading, setPendingGrading] = React.useState(0);
  const [needAttention, setNeedAttention] = React.useState(0);
  const [upcomingDue, setUpcomingDue] = React.useState(0);
  const [attentionList, setAttentionList] = React.useState<AttentionStudent[]>([]);
  const [dueSoonList, setDueSoonList] = React.useState<DueSoon[]>([]);
  React.useEffect(() => {
    if (!user) return;
    (async () => {
      // Lớp do mình dạy
      const { data: classes } = await supabase
        .from("classes").select("id, name").eq("teacher_id", user.id);
      const classIds = (classes ?? []).map(c => c.id);
      const classMap = new Map((classes ?? []).map(c => [c.id, c.name]));
      if (!classIds.length) return;

      const today = new Date();
      const todayStart = new Date(today); todayStart.setHours(0,0,0,0);
      const todayEnd = new Date(today); todayEnd.setHours(23,59,59,999);
      const in7 = new Date(today); in7.setDate(in7.getDate() + 7);

      const [sessRes, upcomingRes, assignRes, subRes, attRes] = await Promise.all([
        supabase.from("class_sessions")
          .select("id, starts_at, ends_at, room, class_id")
          .in("class_id", classIds)
          .gte("starts_at", todayStart.toISOString())
          .lte("starts_at", todayEnd.toISOString())
          .order("starts_at"),
        supabase.from("class_sessions")
          .select("id, starts_at, ends_at, room, class_id")
          .in("class_id", classIds)
          .gt("starts_at", new Date().toISOString())
          .order("starts_at")
          .limit(5),
        supabase.from("assignments")
          .select("id, title, due_at, class_id")
          .in("class_id", classIds),
        supabase.from("submissions")
          .select("id, score, submitted_at, student_id, assignment_id")
          .order("submitted_at", { ascending: false })
          .limit(50),
        supabase.from("attendances").select("status, student_id, session_id"),
      ]);

      const todaySessions: Session[] = (sessRes.data ?? []).map(s => ({
        id: s.id,
        time: format(new Date(s.starts_at), "HH:mm"),
        name: classMap.get(s.class_id) ?? "—",
        room: s.room ?? "—",
        starts_at: new Date(s.starts_at),
        ends_at: new Date(s.ends_at),
      }));
      setSessions(todaySessions);

      setUpcoming((upcomingRes.data ?? []).map(s => ({
        id: s.id,
        name: classMap.get(s.class_id) ?? "—",
        starts_at: new Date(s.starts_at),
        ends_at: new Date(s.ends_at),
        room: s.room,
      })));

      const assignments = assignRes.data ?? [];
      const myAssignIds = new Set(assignments.map(a => a.id));
      const mySubs = (subRes.data ?? []).filter(s => myAssignIds.has(s.assignment_id));

      setPendingGrading(mySubs.filter(s => s.score === null).length);

      const dueSoonAssignments: DueSoon[] = assignments.filter(a => {
        if (!a.due_at) return false;
        const d = new Date(a.due_at);
        return d >= today && d <= in7;
      }).map(a => ({
        title: a.title ?? "Bài tập",
        className: classMap.get(a.class_id) ?? "—",
        daysLeft: Math.ceil((new Date(a.due_at!).getTime() - today.getTime()) / 86400000),
      }));
      setUpcomingDue(dueSoonAssignments.length);
      setDueSoonList(dueSoonAssignments.slice(0, 3));

      // Students need attention: attendance < 50% OR avg score < 5
      const mySessionIds = new Set<string>();
      const { data: allMySess } = await supabase
        .from("class_sessions").select("id").in("class_id", classIds);
      (allMySess ?? []).forEach(s => mySessionIds.add(s.id));
      const myAtt = (attRes.data ?? []).filter(a => mySessionIds.has(a.session_id));
      const byStudent = new Map<string, { total: number; present: number }>();
      for (const a of myAtt) {
        const cur = byStudent.get(a.student_id) ?? { total: 0, present: 0 };
        cur.total++;
        if (a.status === "present" || a.status === "late") cur.present++;
        byStudent.set(a.student_id, cur);
      }

      // Avg score per student (all submissions, not just recent 50)
      const { data: allScored } = myAssignIds.size
        ? await supabase.from("submissions")
            .select("student_id, score")
            .in("assignment_id", Array.from(myAssignIds))
            .not("score", "is", null)
        : { data: [] as { student_id: string; score: number | null }[] };
      const scoreByStudent = new Map<string, { total: number; sum: number }>();
      (allScored ?? []).forEach(s => {
        if (s.score === null) return;
        const cur = scoreByStudent.get(s.student_id) ?? { total: 0, sum: 0 };
        cur.total++;
        cur.sum += Number(s.score);
        scoreByStudent.set(s.student_id, cur);
      });

      // Build name map covering both attendance and submission students
      const allStudentIds = new Set<string>([
        ...byStudent.keys(),
        ...scoreByStudent.keys(),
        ...mySubs.slice(0, 20).map(s => s.student_id),
      ]);
      const { data: profs } = allStudentIds.size
        ? await supabase.from("profiles").select("id, display_name, email").in("id", Array.from(allStudentIds))
        : { data: [] };
      const nameOf = new Map((profs ?? []).map(p => [p.id, p.display_name || p.email.split("@")[0]]));

      const attentionStudents: AttentionStudent[] = [];
      const allIds = new Set([...byStudent.keys(), ...scoreByStudent.keys()]);
      allIds.forEach(studentId => {
        const v = byStudent.get(studentId);
        const attRate = v && v.total > 0 ? v.present / v.total : 1;
        const scoreData = scoreByStudent.get(studentId);
        const avgScore = scoreData ? scoreData.sum / scoreData.total : 10;
        const reasons: string[] = [];
        if (v && v.total >= 3 && attRate < 0.5) reasons.push(`Điểm danh ${Math.round(attRate * 100)}%`);
        if (scoreData && avgScore < 5) reasons.push(`Điểm TB ${avgScore.toFixed(1)}`);
        if (reasons.length > 0) {
          attentionStudents.push({ id: studentId, name: nameOf.get(studentId) ?? "Học sinh", reasons });
        }
      });
      setNeedAttention(attentionStudents.length);
      setAttentionList(attentionStudents.slice(0, 3));

      // Activity feed: recent submissions
      const acts: Activity[] = mySubs.slice(0, 6).map(s => {
        const name = nameOf.get(s.student_id) ?? "Học sinh";
        const graded = s.score !== null;
        const Icon = graded ? BarChart3 : CheckCircle2;
        const iconColor = graded ? "text-blue-500" : "text-emerald-500";
        const text = graded
          ? `Đã chấm bài cho ${name} (${s.score} đ)`
          : `${name} đã nộp bài`;
        return { Icon, iconColor, text, time: timeAgo(new Date(s.submitted_at)) };
      });
      setActivity(acts);
    })();
  }, [user]);

  const today = format(new Date(), "EEEE, dd/MM/yyyy", { locale: vi });
  const firstName = (display.name || "Giáo viên").split(" ").slice(-1)[0];
  const totalHours = sessions.reduce((acc, s) => acc + (s.ends_at.getTime() - s.starts_at.getTime()) / 3600000, 0);

  return (
    <PortalLayout portal="teacher" title={`Chào ${firstName}`} breadcrumb={[{ label: "Giáo viên" }, { label: "Tổng quan" }]}>
      <p className="text-muted-foreground -mt-3 mb-8 capitalize">{today}</p>

      <div className="grid lg:grid-cols-3 gap-5 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 rounded-2xl p-6 text-white relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #1A6B3C, #166534)" }}
        >
          <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full opacity-20 blur-2xl bg-[#4ADE80]" />
          <div className="relative">
            <div className="text-xs uppercase tracking-wider opacity-80 mb-1">Lịch dạy hôm nay</div>
            <h2 className="font-display text-2xl font-semibold mb-5">
              {sessions.length} lớp · {totalHours.toFixed(1)} giờ giảng dạy
            </h2>
            {sessions.length ? (
              <ul className="space-y-2.5">
                {sessions.map((c, i) => {
                  const now = new Date();
                  const isActive = isWithinInterval(now, { start: c.starts_at, end: c.ends_at });
                  const isUpcoming = c.starts_at > now && c.starts_at < addHours(now, 24);
                  return (
                    <motion.li key={c.id}
                      initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.08 }}
                      className="flex items-center gap-4 p-3 rounded-xl bg-white/10 backdrop-blur"
                    >
                      <Clock className="size-4 shrink-0" />
                      <div className="font-medium w-14">{c.time}</div>
                      <div className="flex-1">
                        <div className="font-semibold">{c.name}</div>
                        <div className="text-xs opacity-80">Phòng {c.room}</div>
                      </div>
                      {isActive ? (
                        <Link to="/teacher/attendance">
                          <motion.button
                            animate={{ boxShadow: ["0 0 0 0 rgba(74,222,128,0.7)", "0 0 0 12px rgba(74,222,128,0)"] }}
                            transition={{ repeat: Infinity, duration: 1.8 }}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#4ADE80] text-emerald-950"
                          >Điểm danh</motion.button>
                        </Link>
                      ) : (
                        <Badge className="bg-white/20 text-white hover:bg-white/20 border-0 text-xs">
                          {isUpcoming ? "Sắp tới" : "Đã xong"}
                        </Badge>
                      )}
                    </motion.li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm opacity-80">Không có buổi học nào hôm nay.</p>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="bg-card border rounded-2xl p-6 shadow-sm"
        >
          <h3 className="font-display font-semibold mb-4">Hoạt động gần đây</h3>
          {activity.length ? (
            <ul className="space-y-3 max-h-[280px] overflow-y-auto scrollbar-thin">
              {activity.map((a, i) => (
                <motion.li key={i}
                  initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.05 }}
                  className="flex items-start gap-2.5"
                >
                  <a.Icon className={`size-4 mt-0.5 shrink-0 ${a.iconColor}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm leading-snug">{a.text}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{a.time}</div>
                  </div>
                </motion.li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Chưa có hoạt động.</p>
          )}
        </motion.div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <Link to="/teacher/grading" search={{ filter: undefined }}>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            whileHover={{ y: -4 }}
            className="bg-card border rounded-2xl p-5 flex items-center gap-4 shadow-sm hover:shadow-lg transition-shadow h-full">
            <div className="size-12 rounded-xl grid place-items-center bg-rose-100">
              <FileText className="size-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Bài chưa chấm</div>
              <div className="font-display text-3xl font-semibold">{pendingGrading}</div>
            </div>
          </motion.div>
        </Link>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.38 }}
          className="bg-card border rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="size-10 rounded-xl grid place-items-center bg-amber-100">
              <AlertTriangle className="size-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">HS cần chú ý</div>
              <div className="font-display text-2xl font-semibold">{needAttention}</div>
            </div>
          </div>
          {attentionList.length > 0 && (
            <div className="space-y-1.5 mb-3">
              {attentionList.map((s, i) => (
                <div key={i} className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <span className="size-1.5 rounded-full bg-amber-500 shrink-0" />
                  <span className="truncate">{s.name}</span>
                  <span className="text-[10px] opacity-70">· {s.reasons.join(", ")}</span>
                </div>
              ))}
            </div>
          )}
          <Link to="/teacher/analytics" search={{ filter: "attention" }}
            className="text-xs text-blue-600 hover:underline">Xem tất cả →</Link>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.46 }}
          className="bg-card border rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="size-10 rounded-xl grid place-items-center bg-sky-100">
              <CheckCircle2 className="size-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Bài sắp đến hạn</div>
              <div className="font-display text-2xl font-semibold">{upcomingDue}</div>
            </div>
          </div>
          {dueSoonList.length > 0 && (
            <div className="space-y-1.5 mb-3">
              {dueSoonList.map((a, i) => (
                <div key={i} className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <span className="size-1.5 rounded-full bg-sky-500 shrink-0" />
                  <span className="truncate">{a.title}</span>
                  <span className="text-[10px] opacity-70">· còn {a.daysLeft} ngày</span>
                </div>
              ))}
            </div>
          )}
          <Link to="/teacher/assignments" className="text-xs text-blue-600 hover:underline">Xem tất cả →</Link>
        </motion.div>
      </div>

      <div className="bg-card border rounded-2xl p-5 shadow-sm mt-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold text-sm">Lịch sắp tới</h3>
          <Link to="/teacher/schedule" className="text-xs text-blue-600 hover:underline">Xem tất cả →</Link>
        </div>
        {upcoming.length === 0 ? (
          <p className="text-sm text-muted-foreground">Không có buổi dạy nào sắp tới.</p>
        ) : (
          <ul className="divide-y">
            {upcoming.map(u => (
              <li key={u.id} className="flex items-center gap-3 py-2.5 text-sm">
                <Clock className="size-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{u.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {format(u.starts_at, "EEE dd/MM · HH:mm", { locale: vi })}–{format(u.ends_at, "HH:mm")}
                    {u.room ? ` · P.${u.room}` : ""}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </PortalLayout>
  );
}
